import { FC } from 'react';

import { GiftChannelLedgerType } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { Link } from 'react-router-dom';

import { ChannelBalanceCards } from '../components/channelLedger/ChannelBalanceCards';
import { ChannelLedgerManualModal } from '../components/channelLedger/ChannelLedgerManualModal';
import { ChannelLedgerTable } from '../components/channelLedger/ChannelLedgerTable';
import { UnrecordedSection } from '../components/channelLedger/UnrecordedSection';
import { Toast } from '../components/Toast';
import { useChannelLedgerPage } from '../hooks/useChannelLedgerPage';

const MANUAL_ACTIONS: Array<{ type: GiftChannelLedgerType; label: string }> = [
  { type: GiftChannelLedgerType.Deposit, label: '+ 충전 입금' },
  { type: GiftChannelLedgerType.ExternalUse, label: '− 무관 사용' },
  { type: GiftChannelLedgerType.Restore, label: '+ 취소·환급' },
  { type: GiftChannelLedgerType.Adjust, label: '± 보정' },
];

/**
 * 발송매체 충전금 미러 원장 — 채널별 선충전 잔액 카피본.
 * 발송 차감은 자동 기장되고, 입금·무관사용은 여기서 수동 기록한다.
 * "미러 잔액 == 실제 사이트 잔액" 대조가 어긋나면 실수(도매가 오입력 등) 신호.
 */
export const GiftChannelLedgerPage: FC = function GiftChannelLedgerPage() {
  const {
    isAdminUser,
    supported,
    balances,
    selectedChannel,
    setSelectedChannel,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    ledger,
    ledgerLoading,
    reconcileInputs,
    setReconcileInput,
    reconcileResults,
    reconcilingChannel,
    handleReconcile,
    manualModal,
    setManualModal,
    openManualModal,
    submitManualModal,
    deletingId,
    handleDeleteEntry,
    unrecordedOpen,
    setUnrecordedOpen,
    unrecordedFrom,
    setUnrecordedFrom,
    unrecordedTo,
    setUnrecordedTo,
    unrecorded,
    unrecordedLoading,
    fetchUnrecorded,
    isRepairing,
    handleRepairUnrecorded,
    toastMessage,
    toastDurationMs,
  } = useChannelLedgerPage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold">매체 충전금 원장</h1>
            <p className="mt-1 text-sm text-gray-500">
              발송매체 선충전 잔액의 미러 원장 — 발송 처리 후 실제 사이트 잔액과 대조해
              도매가 오입력·입금 미기록을 잡습니다. 발송 차감은 자동 기장됩니다.
            </p>
          </div>
          <Link
            to="/gift-seller/send"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            발송 대기 목록
          </Link>
        </div>
      </div>

      {supported === null ? (
        <div className="center-box py-20">
          <Loading />
        </div>
      ) : supported === false ? (
        <div className="mx-4 mt-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-6 text-center text-sm text-amber-700">
          서버가 아직 충전금 원장을 지원하지 않습니다 — 백엔드 배포 후 사용할 수 있습니다.
        </div>
      ) : (
        <div className="space-y-4 p-4">
          {/* 수동 기장 버튼 */}
          <div className="flex flex-wrap items-center gap-2">
            {MANUAL_ACTIONS.map((action) => (
              <button
                key={action.type}
                type="button"
                onClick={() => openManualModal(action.type, selectedChannel)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {action.label}
              </button>
            ))}
            <span className="ml-1 text-xs text-gray-400">
              발송 차감은 발송 처리에서 자동 기장 — 여기서는 입금·무관사용·환급·보정만
              입력합니다.
            </span>
          </div>

          {/* 채널별 잔액 + 대사 */}
          {balances && (
            <ChannelBalanceCards
              balances={balances}
              reconcileInputs={reconcileInputs}
              setReconcileInput={setReconcileInput}
              reconcileResults={reconcileResults}
              reconcilingChannel={reconcilingChannel}
              onReconcile={handleReconcile}
              onOpenManual={openManualModal}
            />
          )}

          {/* 원장 목록 — 채널 탭 + 기간 필터 */}
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {DELIVERY_CHANNEL_LIST.map((channel) => {
                const config = DELIVERY_CHANNEL_CONFIG[channel];
                const isActive = selectedChannel === channel;
                return (
                  <button
                    key={channel}
                    type="button"
                    onClick={() => setSelectedChannel(channel)}
                    className={`rounded-full border px-3 py-1 text-sm font-medium ${
                      isActive
                        ? `${config.badgeClass} ring-2 ring-indigo-300`
                        : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {config.label}
                  </button>
                );
              })}

              <div className="ml-auto flex items-center gap-1.5">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                />
                <span className="text-xs text-gray-400">~</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                />
                {(fromDate || toDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFromDate('');
                      setToDate('');
                    }}
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
                  >
                    전체
                  </button>
                )}
              </div>
            </div>

            {ledgerLoading ? (
              <div className="center-box py-12">
                <Loading />
              </div>
            ) : !ledger || ledger.entries.length === 0 ? (
              <div className="rounded-lg border bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
                {DELIVERY_CHANNEL_CONFIG[selectedChannel].label} 원장 기록이 없습니다.
              </div>
            ) : (
              <ChannelLedgerTable
                ledger={ledger}
                hasFromFilter={!!fromDate}
                isAdminUser={isAdminUser}
                deletingId={deletingId}
                onDelete={handleDeleteEntry}
              />
            )}
          </div>

          {/* 미기장 점검 (접이식) */}
          <UnrecordedSection
            open={unrecordedOpen}
            setOpen={setUnrecordedOpen}
            from={unrecordedFrom}
            setFrom={setUnrecordedFrom}
            to={unrecordedTo}
            setTo={setUnrecordedTo}
            result={unrecorded}
            loading={unrecordedLoading}
            onFetch={fetchUnrecorded}
            isRepairing={isRepairing}
            onRepair={handleRepairUnrecorded}
          />
        </div>
      )}

      {manualModal && (
        <ChannelLedgerManualModal
          modal={manualModal}
          setModal={setManualModal}
          onSave={submitManualModal}
        />
      )}

      <Toast message={toastMessage} durationMs={toastDurationMs} />
    </div>
  );
};
